import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTable, useExpanded } from 'react-table';
import { Column } from '../../styles';
import { Wrapper } from './styles';
import { columns, CellWithBackground, changedValueCellHexColor } from './table-config';
import HistoryTable from '../HistoryTable';
import { ToggleSubcomponent } from '../../Table/table-config';
import EditableCell from '../../../EditableCell';
import { parseToFloatIfNumber } from '../../utils';
import { round, trim } from 'lodash';

const GROUP_NAMES = ["eneral", "tilities", "2D", "3D", "uctural"];

const historyTableEmptyRow = {
  criteria: '',
  phase: '',
  score: '',
  reasoning: '',
  // for the popup info
  projectName: null,
  phaseDescription: null,
};

const Table = ({
  groups = [],
  historyTables = [],
  totalRow = {},
  updateScoringCellData,
  setScoringTablePrimitiveField,
  isAdmin,
  isBespokeModule,
}) => {
  const currentGroupByIndex = useRef(0);
  const [isTotalRowExpanded, toggleTotalRow] = useState(false);

  const rowsData = useMemo(() => {
    const allRows = groups.sort((g1, g2) => {
      // Find the index of each string in the control array
      const indexA = GROUP_NAMES.findIndex(substring => g1.groupName.includes(substring));
      const indexB = GROUP_NAMES.findIndex(substring => g2.groupName.includes(substring));
      // Compare the indexes and return the result
      return indexA - indexB;
    }).flatMap((group) => group.rows);
    return allRows;
  }, [JSON.stringify(groups)]);

  const historyTableRows = useMemo(() => {
    const length = historyTables[0]?.length ?? 0;
    const historyRows = [];
    for (let i = 0; i < length; i++) {
      const row = historyTables.map((historyTable) => historyTable.at(i));
      historyRows.push(row);
    }
    return historyRows;
  }, [historyTables]);

  const groupRows = useMemo(() => {
    return groups.map((group) => ({
      groupName: group.groupName,
      groupWeight: group.groupWeight,
      totalRows: group.rows.length,
    }));
  }, []);

  const updateCellData = (rowIndex, columnId, value) => {
    let groupIndex = 0;
    let totalIteratedRows = 0;
    let iteratedRowsInGroup = 0;

    while (true) {
      if (totalIteratedRows === rowIndex) {
        break;
      }

      iteratedRowsInGroup++;
      totalIteratedRows++;

      if (iteratedRowsInGroup === groupRows[groupIndex].totalRows) {
        groupIndex++;
        iteratedRowsInGroup = 0;
      }
    }

    const totalRowsBeforeGroup = groupRows
      .slice(0, groupIndex)
      .map((group) => group.totalRows)
      .reduce((total, curr) => total + curr, 0);

    let rowIndexInGroup = rowIndex - totalRowsBeforeGroup;

    const _value = parseToFloatIfNumber(value);
    updateScoringCellData(groupIndex, rowIndexInGroup, rowIndex, columnId, _value);
  };

  const getLatestRowData = (rowInd) => {
    return historyTableRows[rowInd] ? historyTableRows[rowInd][0] : null;
  };

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, footerGroups, visibleColumns } = useTable(
    {
      columns: columns,
      data: rowsData,
      autoResetExpanded: false,
      updateCellData,
      isAdmin,
      isBespokeModule,
      getLatestRowData,
    },
    useExpanded
  );

  const shouldRenderGroup = (rowInd) => {
    const neededToBeRenderedFirst = groupRows
      .slice(0, currentGroupByIndex.current)
      .map(({ totalRows }) => totalRows)
      .reduce((total, curr) => total + curr, 0);
    return rowInd >= neededToBeRenderedFirst;
  };

  const latestTotalRowHistoryData = totalRow.historyRows[0];
  const isTotalRowReasoningChanged =
    latestTotalRowHistoryData && trim(totalRow.reasoning) !== trim(latestTotalRowHistoryData.reasoning);

  return (
    <Wrapper>
      {!isBespokeModule && <table
        {...getTableProps()}
        className='table-scoring'>
        <thead className='thead-scoring'>
          {headerGroups.map((headerGroup, ind) => (
            <tr
              key={ind}
              {...headerGroup.getHeaderGroupProps()}
              className='tr-scoring'>
              {headerGroup.headers.map((column) => (
                <th
                  className='th-scoring'
                  {...column.getHeaderProps({
                    style: column.getCustomStyles && column.getCustomStyles(),
                  })}
                  id={column.id}
                  rowSpan={`${column.rowSpan ?? 1}`}
                  colSpan={`${column.colSpan ?? column.getHeaderProps().colSpan}`}>
                  {column.render('Header')}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody
          {...getTableBodyProps()}
          className='tbody-scoring'>
          {rows.map((row, rowInd) => {
            prepareRow(row);
            const rowProps = row.getRowProps();
            return (
              <React.Fragment key={rowProps.key}>
                <tr
                  {...rowProps}
                  className='tr-scoring'>
                  {row.cells.map((cell, cellInd) => {
                    const columnStyles = cell.column.getCustomStyles && cell.column.getCustomStyles();
                    if (cellInd !== 0) {
                      return (
                        <td
                          key={cellInd}
                          className='td-scoring'
                          {...cell.getCellProps({
                            style: {
                              minWidth: columnStyles?.minWidth,
                              width: columnStyles?.width,
                              maxWidth: columnStyles?.maxWidth,
                              padding: columnStyles?.padding,
                            },
                          })}>
                          {cell.render('Cell')}
                        </td>
                      );
                    }
                    let td = <td className='td-scoring hide'></td>;
                    if (shouldRenderGroup(rowInd)) {
                      const {
                        groupName,
                        groupWeight,
                        totalRows: groupTotalRows,
                      } = groupRows[currentGroupByIndex.current++];
                      const numOfExpRows = rows
                        .slice(rowInd, rowInd + groupTotalRows)
                        .filter((r) => r.isExpanded).length;
                      td = (
                        <th
                          className='th-scoring'
                          role='rowgroup'
                          rowSpan={groupTotalRows + numOfExpRows}
                          {...cell.getCellProps({
                            style: {
                              minWidth: columnStyles?.minWidth,
                              width: columnStyles?.width,
                              maxWidth: columnStyles?.maxWidth,
                            },
                          })}>
                          <Column>
                            <span>{groupName}</span>
                            <span>{groupWeight}%</span>
                          </Column>
                        </th>
                      );
                    }
                    if (rows.length - 1 === rowInd) {
                      currentGroupByIndex.current = 0;
                    }
                    return td;
                  })}
                </tr>
                {row.isExpanded && (
                  <tr>
                    <td
                      colSpan={visibleColumns.length - 1}
                      className='td-expanded-scoring'>
                      <div>
                        <HistoryTable rowsData={historyTableRows[rowInd]} />
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
          {/* TOTALS ROW  - TODO REFACTOR IT !!!!!*/}
          <React.Fragment>
            <tr>
              <th
                style={{ opacity: 0 }}
                className='td-scoring'></th>
              <td className='td-scoring'>100%</td>
              <td className='td-scoring td-criteria'>Combined module score</td>
              <td className='td-scoring'>Phase {totalRow.phase}</td>
              <td className='td-scoring'>{round(totalRow.score)}%</td>
              <td
                className='td-scoring'
                style={{ padding: 0 }}>
                <CellWithBackground color={isTotalRowReasoningChanged && changedValueCellHexColor}>
                  {!isAdmin ? (
                    totalRow.reasoning
                  ) : (
                    <EditableCell
                      // this is init value, it updates onBlur
                      value={totalRow.reasoning}
                      row={{ index: -1 }}
                      column={{ id: 'reasoning' }}
                      updateCellData={(_, __, value) => setScoringTablePrimitiveField('totalReasoning', value)}
                      isDisabled={isBespokeModule}
                    />
                  )}
                </CellWithBackground>
              </td>
              <td className='td-scoring'>
                <ToggleSubcomponent
                  isExpanded={isTotalRowExpanded}
                  onClick={() => toggleTotalRow((currVal) => !currVal)}
                />
              </td>
            </tr>
            {isTotalRowExpanded && (
              <tr className='tr-total-expanded-scoring'>
                <th></th>
                <td
                  colSpan={visibleColumns.length - 1}
                  className='td-total-expanded-scoring'>
                  <div>
                    <HistoryTable rowsData={totalRow.historyRows} />
                  </div>
                </td>
              </tr>
            )}
          </React.Fragment>
        </tbody>
      </table>}
    </Wrapper>
  );
};

export default Table;
